/*
 * CredentialingIncidentsModal — pop-up viewer for the MedexOva event stream.
 *
 * Opened from the admin nav item "Credentialing Feed". Shows the last 100
 * MedexOva webhook events (cleared / rejected / lapsed / expiring_soon / etc)
 * with color-coded severity, a "refresh" button, and event-detail expansion.
 *
 * NOTE: this is a MODAL, not a routed page — mounted at AdminDashboard level
 * and toggled via `open` prop. Keeps navigation state clean.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { ShieldCheck, ShieldAlert, RefreshCw, X, Clock, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const SEVERITY = {
  // Green — good news
  "driver.credentialing.cleared":   { tone: "green",  Icon: CheckCircle2 },
  "driver.credentialing.restored":  { tone: "green",  Icon: CheckCircle2 },
  "tp.credentialing.cleared":       { tone: "green",  Icon: CheckCircle2 },
  "tp.credentialing.restored":      { tone: "green",  Icon: CheckCircle2 },
  "insurance.active":               { tone: "green",  Icon: CheckCircle2 },
  // Amber — attention
  "driver.credentialing.in_review": { tone: "amber",  Icon: Clock },
  "driver.credentialing.expiring_soon": { tone: "amber", Icon: AlertTriangle },
  "tp.credentialing.in_review":     { tone: "amber",  Icon: Clock },
  "tp.credentialing.expiring_soon": { tone: "amber",  Icon: AlertTriangle },
  "insurance.expiring_soon":        { tone: "amber",  Icon: AlertTriangle },
  // Red — dispatch-blocking
  "driver.credentialing.rejected":  { tone: "red",    Icon: XCircle },
  "driver.credentialing.lapsed":    { tone: "red",    Icon: ShieldAlert },
  "tp.credentialing.rejected":      { tone: "red",    Icon: XCircle },
  "tp.credentialing.lapsed":        { tone: "red",    Icon: ShieldAlert },
  "insurance.declined":             { tone: "red",    Icon: XCircle },
  "insurance.expired":              { tone: "red",    Icon: ShieldAlert },
  "insurance.cancelled":            { tone: "red",    Icon: XCircle },
  "insurance.claim_filed":          { tone: "red",    Icon: AlertTriangle },
};
const TONE_CLS = {
  green: "bg-green-50 text-green-800 border-green-200",
  amber: "bg-amber-50 text-amber-900 border-amber-200",
  red:   "bg-red-50   text-red-800   border-red-200",
  slate: "bg-slate-50 text-slate-700 border-slate-200",
};

function shortRef(ref) {
  if (!ref) return "—";
  if (ref.startsWith("astat_drv_")) return `driver ${ref.slice(10, 18)}…`;
  if (ref.startsWith("astat_tp_"))  return `TP ${ref.slice(9, 17)}…`;
  return ref.slice(0, 24);
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export default function CredentialingIncidentsModal({ open, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | drivers | tps | red | amber

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/medexova/events", { params: { limit: 100 } });
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load events"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const filtered = useMemo(() => {
    return events.filter(e => {
      const type = e.event || "";
      if (filter === "drivers") return type.startsWith("driver.") || type.startsWith("insurance.");
      if (filter === "tps") return type.startsWith("tp.");
      if (filter === "red") return (SEVERITY[type]?.tone) === "red";
      if (filter === "amber") return (SEVERITY[type]?.tone) === "amber";
      return true;
    });
  }, [events, filter]);

  const counts = useMemo(() => ({
    total: events.length,
    red: events.filter(e => SEVERITY[e.event]?.tone === "red").length,
    amber: events.filter(e => SEVERITY[e.event]?.tone === "amber").length,
    green: events.filter(e => SEVERITY[e.event]?.tone === "green").length,
  }), [events]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="credentialing-incidents-modal">
        {/* Header */}
        <div className="p-5 border-b border-[#E9EAE4] flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-forest"/>
          <div className="flex-1">
            <div className="text-lg font-bold text-slate_navy">Credentialing Incidents Feed</div>
            <div className="text-xs text-slate-500">Live stream of MedexOva compliance events for every driver and TP in your network.</div>
          </div>
          <button onClick={load} disabled={loading} className="text-slate-500 hover:text-slate-800 p-1.5" data-testid="incidents-refresh" title="Refresh">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""}/>
          </button>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 p-1.5" data-testid="incidents-close" title="Close">
            <X size={18}/>
          </button>
        </div>

        {/* Counts + filter chips */}
        <div className="px-5 py-3 border-b border-[#F1EFE8] flex items-center gap-2 flex-wrap text-xs">
          {[
            { k: "all",    label: `All · ${counts.total}`,       cls: "bg-slate-100 text-slate-800"},
            { k: "red",    label: `🔴 Blocking · ${counts.red}`,  cls: "bg-red-100 text-red-800"},
            { k: "amber",  label: `🟠 Attention · ${counts.amber}`, cls: "bg-amber-100 text-amber-900"},
            { k: "drivers", label: "Drivers only",  cls: "bg-slate-100 text-slate-700"},
            { k: "tps",    label: "TPs only",       cls: "bg-slate-100 text-slate-700"},
          ].map(chip => (
            <button
              key={chip.k}
              onClick={() => setFilter(chip.k)}
              className={`px-2.5 py-1 rounded-md font-semibold border ${filter === chip.k ? "ring-2 ring-forest border-forest " + chip.cls : "border-[#E2E0D8] " + chip.cls + " opacity-70"}`}
              data-testid={`incidents-filter-${chip.k}`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Feed */}
        <div className="flex-1 overflow-y-auto">
          {loading && events.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">Loading events…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="incidents-empty">
              {events.length === 0
                ? "No credentialing events yet. Events appear here as soon as MedexOva sends webhook activity."
                : "No events match this filter."}
            </div>
          )}
          <ul className="divide-y divide-[#F1EFE8]">
            {filtered.map((e, i) => {
              const meta = SEVERITY[e.event] || { tone: "slate", Icon: Clock };
              const { Icon } = meta;
              const isOpen = expandedId === (e.event_id + i);
              const details = e.payload || {};
              return (
                <li key={e.event_id + i} className="px-5 py-3 hover:bg-slate-50" data-testid={`incident-row-${e.event_id || i}`}>
                  <button
                    onClick={() => setExpandedId(isOpen ? null : (e.event_id + i))}
                    className="w-full flex items-start gap-3 text-left"
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-full grid place-items-center border ${TONE_CLS[meta.tone]}`}>
                      <Icon size={15}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 text-sm">{e.event}</span>
                        <span className="text-[11px] text-slate-500">· {shortRef(e.external_ref)}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {timeAgo(e.received_at)} · <span className="font-mono">{(e.event_id || "").slice(0, 12)}</span>
                        {details.reason && <span className="ml-2">· {String(details.reason).slice(0, 80)}</span>}
                        {details.field && <span className="ml-2">· {details.field}</span>}
                        {details.days_until != null && <span className="ml-2 font-semibold">in {details.days_until}d</span>}
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400">{isOpen ? "▼" : "▶"}</span>
                  </button>
                  {isOpen && (
                    <pre className="mt-2 ml-11 bg-slate-50 border border-[#E9EAE4] rounded-md p-2 text-[10px] text-slate-700 overflow-x-auto max-h-56" data-testid={`incident-detail-${e.event_id || i}`}>
{JSON.stringify(details, null, 2)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer note */}
        <div className="px-5 py-2 bg-slate-50 border-t border-[#E9EAE4] text-[10px] text-slate-500">
          Events are stored append-only in <code>db.medexova_events</code>. Cleared drivers/TPs can be dispatched; lapsed or rejected records are automatically blocked from dispatch.
        </div>
      </div>
    </div>
  );
}
